import React, { useRef, useState } from "react";

import { TaxType } from "@/custom-types/Cart";
import { useSelector } from "@/redux/hooks";
import { getDispensaryTaxType as getCartDispensaryTaxType } from "@/redux/selectors/cartDispensary";
import { getOrderTaxType } from "@/redux/selectors/order";

import LabeledItem from "../LabeledItem";
import TaxModal from "../TaxModal";

type ItemizedLabelItemsProps = {
  taxType: TaxType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  cartTotals: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  [x: string]: any;
};

type SummarizedLabelItemProps = {
  taxType: TaxType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  cartTotals: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  [x: string]: any;
};

type TaxTotalProps = {
  itemize: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  cartTotals: any;
  orderSummary: boolean;
};

const ITEMIZED_TAXES = [
  {
    key: "salesTax",
    label: "Est. sales tax",
  },
  {
    key: "exciseTax",
    label: "Est. excise tax",
  },
  {
    key: "otherTax",
    label: "Est. additional tax",
  },
];

const ItemizedLabelItems = React.forwardRef<
  HTMLDivElement,
  ItemizedLabelItemsProps
>(({ cartTotals = {}, ...taxItemProps }, ref) => {
  return (
    <>
      {ITEMIZED_TAXES.map((tax) =>
        cartTotals[tax.key] !== null ? (
          <LabeledItem
            key={tax.key}
            {...taxItemProps}
            ref={ref}
            label={tax.label}
          >
            ${cartTotals[tax.key]}
          </LabeledItem>
        ) : null,
      ).filter((tax) => tax !== null)}
    </>
  );
});

const SummarizedLabelItems = React.forwardRef<
  HTMLDivElement,
  SummarizedLabelItemProps
>(({ taxType, cartTotals = {}, ...taxItemProps }, ref) => {
  const getEstTaxLabel = () => {
    switch (taxType) {
      case TaxType.CalculatedAtPayment:
        return "Calculated at payment";
      case TaxType.IncludedInMenu:
        return "Included in total";
      default:
        return cartTotals.tax > 0
          ? `$${cartTotals.tax}`
          : "Calculated at payment";
    }
  };

  return (
    <React.Fragment>
      <LabeledItem
        {...taxItemProps}
        aria-label={`estimated tax is ${getEstTaxLabel().toLowerCase()}`}
        label="Est. Taxes"
        ref={ref}
      >
        {getEstTaxLabel()}
      </LabeledItem>
    </React.Fragment>
  );
});

const TaxTotal = ({ itemize, cartTotals, orderSummary }: TaxTotalProps) => {
  // TODO: uncomment to include reservation taxType when reservation selectors are migrated in the future
  //   const dispensaryTaxType = useSelector(dispensaryTaxTypeSelector);
  //   const reservationTaxType = useSelector(reservationTaxTypeSelector);
  //   const taxType = reservationTaxType || dispensaryTaxType;

  // placeholder until above code block is uncommente as getCartDispensaryTaxTyped

  const orderTaxType = useSelector(getOrderTaxType);
  const orderCartDispensaryType = useSelector(getCartDispensaryTaxType);
  const taxType = orderSummary ? orderTaxType : orderCartDispensaryType;

  const taxItemRef = useRef(null);

  const [showTaxModal, setShowTaxModal] = useState(false);

  const toggleShowTaxModal = () => {
    setShowTaxModal(!showTaxModal);
  };

  const taxItemProps = {
    className: "pt-sm",
    icon: "info",
    onClick: toggleShowTaxModal,
    ref: taxItemRef,
  };

  const showItemized =
    itemize &&
    !!ITEMIZED_TAXES.find((t) => cartTotals[t.key] !== null) &&
    taxType !== TaxType.CalculatedAtPayment &&
    taxType !== TaxType.IncludedInMenu;

  return (
    <React.Fragment>
      {showItemized ? (
        <ItemizedLabelItems {...taxItemProps} cartTotals={cartTotals} />
      ) : (
        <SummarizedLabelItems
          {...taxItemProps}
          taxType={taxType}
          cartTotals={cartTotals}
        />
      )}

      {showTaxModal && (
        <TaxModal
          returnFocusRef={taxItemRef}
          toggleShowTaxModal={toggleShowTaxModal}
        />
      )}
    </React.Fragment>
  );
};

export default TaxTotal;
